'use client';

import heroBackground from '@/public/home/heroBackground.png';
import Image from 'next/image';
import { useRef } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton } from '@/sanity/lib/definitions';
import { GROQImage } from '@/sanity/lib/types';
import TrustChip from '@/components/global/TrustChip';
import { CTAButton } from '@/shadcn/ui/button';
import HeroCard from '@/components/global/HeroCard';


interface IHeroProps {
  title: PortableTextBlock[];
  buttonOne: GROQButton;
  buttonTwo: GROQButton;
  card: {
    text: PortableTextBlock[];
    image: GROQImage;
  };
}

export default function Hero({
  title,
  buttonOne,
  buttonTwo,
  card,
}: IHeroProps) {
  const isMobile = useIsMobile();

  return (
    <section className="w-full flex flex-col items-center lg:pt-[3rem]">
      <div className="relative flex justify-between flex-col w-full max-w-[96vw] lg:max-w-[230.01rem] min-h-[100rem] lg:min-h-[117.74988rem] overflow-clip lg:px-[6rem] pb-[4.15rem] lg:py-[10rem] lg:items-start items-center">
        <div className="container mt-auto w-full relative z-10 flex flex-col gap-[3rem] lg:gap-[9.13rem] px-[1rem] lg:px-0">
          <div className="flex flex-col gap-[3rem] lg:gap-[2.25rem] w-[min(85.125rem,100%)] mx-auto lg:mx-0">
            <motion.div
              className="flex flex-col gap-[2rem] lg:gap-[2.25rem] prose-headings:text-[5.65rem] lg:prose-headings:text-[7.5rem] prose-headings:mb-0 prose-headings:leading-[110%] lg:prose-headings:leading-[120%] lg:prose-p:max-w-[79rem] lg:text-start text-center [&_br]:hidden lg:[&_br]:block"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: easeInOut, delay: 0.25 }}
              whileInView="visible"
              viewport={{ once: true }}
            >
              <PortableText value={title} components={richTextComponent} />
            </motion.div>

            <motion.div
              className="flex flex-col lg:flex-row gap-[1rem] lg:gap-[3rem] items-center lg:mt-[1.25rem]"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, ease: easeInOut, delay: 0.5 }}
              whileInView="visible"
              viewport={{ once: true }}
            >
              <CTAButton data={buttonOne} className="w-full lg:w-auto" />
              <CTAButton data={buttonTwo} className="w-full lg:w-auto" />
            </motion.div>
          </div>

          <div className="flex flex-col lg:flex-row gap-[2.25rem] items-end justify-between w-full">
            <div className="flex flex-row gap-[1.5rem] items-center px-[1rem] lg:px-0">
              <TrustChip className="flex-shrink-0" />
              <motion.p
                className="text-[1.625rem] lg:text-[1.635rem] text-[#072922] leading-[120%] font-[400] prose-strong:font-[600]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, ease: easeInOut, delay: 0.5 }}
                whileInView="visible"
                viewport={{ once: true }}
              >
                Trusted by <strong>120+ B2B teams</strong> across SaaS, Fintech & Professional Services
              </motion.p>
            </div>

            {card && (
              <motion.div
                initial={{ opacity: 0, y: 300 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.25 }}
                viewport={{ once: true }}
                className="w-[min(40.625rem,100%)] hidden lg:block"
              >
                <HeroCard
                  text={card.text}
                  image={card.image}
                />
              </motion.div>
            )}
          </div>
        </div>

        {/* Background Start */}
        <motion.div
          initial={{ x: 0 }}
          animate={{ x: '-175vw'	 }}
          transition={{ duration: 1.8, ease: [0.76, 0, 0.24, 1] }}
          whileInView="visible"
          viewport={{ once: true }}
          className="absolute w-[150vw] h-full bg-gradient-to-r from-offwhite from-75% to-transparent left-0 z-[1] pointer-events-none select-none bottom-0"
        ></motion.div>

        <svg className="absolute w-[30.5rem] h-auto scale-[.82] lg:scale-100 top-[-3.5rem] lg:top-[-.125rem] right-[-19.75rem] lg:right-[-0.1rem] z-[1]" viewBox="0 0 243 106" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M242.08 105.999V0H0C2.20914 0 4.00003 1.79086 4.00003 4V98C4.00003 100.209 5.79103 102 8.00006 102H238.001C240.209 102 242 103.79 242.001 105.999H242.08Z" fill="#F6F4E9"/>
        </svg>

        <svg className="absolute bottom-[-0.2rem] right-[-0.1rem] z-[1] hidden lg:block" width="390" height="385" viewBox="0 0 390 385" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M389.569 -0.000239566C389.569 1.07046 389.139 2.09605 388.377 2.84771L2.72392 382.989C1.97553 383.727 1.05081 383.741 0 383.741H0.443848L389.569 384.143V-0.000239566Z" fill="#F6F4E9"/>
        </svg>

        <motion.div
          className="absolute inset-x-0 lg:inset-0 top-[1.25rem] lg:top-auto h-[55rem] lg:h-full z-0 pointer-events-none select-none overflow-clip"
          initial={{ scale: 2.5 }}
          animate={{ scale: 1 }}
          transition={{
            duration: 1.25,
            ease: easeInOut,
          }}
          viewport={{ once: true, margin: '-100px' }}
        >
          <div className="absolute bottom-[-.125rem] left-0 w-full h-[30rem] z-10 pointer-events-none select-none bg-gradient-to-t from-offwhite to-transparent lg:hidden"/>
          <Image
            src={heroBackground}
            alt="Hero Background"
            layout="fill"
            objectFit="cover"
            quality={100}
            loading="lazy"
            className="translate-y-[-3rem] lg:translate-y-0 object-[58%_50%] lg:object-center scale-[1.20] lg:scale-100 opacity-70 lg:opacity-100"
          />
        </motion.div>
        {/* Background End */}
      </div>
    </section>
  );
}
